import { useState, useEffect, useCallback, useRef } from "react";
import { getToken } from "@/lib/tokenStorage";
import { logger } from "@/lib/logger";

const WS_RECONNECT_BASE = 1000; // 1s
const WS_RECONNECT_MAX = 30000; // 30s

export default function useSentinelWebSocket() {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimeout = useRef(null);
  const connectRef = useRef(null);
  const listenersRef = useRef({
    new_post: [],
    aggregation_update: [],
    status: [],
    heartbeat: [],
  });

  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backendUrl.replace(/^http/, "ws");
    const token = getToken();
    return `${wsBase}/api/ws/sentinel${token ? `?token=${token}` : ''}`;
  }, []);

  // scheduleReconnect defined before connect — uses connectRef to break circular dep
  const scheduleReconnect = useCallback(() => {
    const delay = Math.min(
      WS_RECONNECT_BASE * Math.pow(2, reconnectAttempt.current),
      WS_RECONNECT_MAX
    );
    reconnectAttempt.current += 1;

    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    reconnectTimeout.current = setTimeout(() => {
      connectRef.current?.();
    }, delay);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = getWsUrl();
    const ws = new WebSocket(url);

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        setLastMessage(msg);

        // Dispatch to type-specific listeners
        const handlers = listenersRef.current[msg.type];
        if (handlers) {
          handlers.forEach((fn) => fn(msg.data));
        }
      } catch (e) {
        logger.warn("[WS] Failed to parse message:", e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, [getWsUrl, scheduleReconnect]);

  // Keep connectRef in sync so scheduleReconnect always calls the latest connect
  connectRef.current = connect;

  // Register a listener for a specific message type
  const on = useCallback((type, callback) => {
    if (!listenersRef.current[type]) {
      listenersRef.current[type] = [];
    }
    listenersRef.current[type].push(callback);

    // Return unsubscribe function
    return () => {
      listenersRef.current[type] = listenersRef.current[type].filter(
        (fn) => fn !== callback
      );
    };
  }, []);

  // Send a message to the server
  const send = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected, lastMessage, on, send };
}
